import type {
    AnchorHTMLAttributes,
    ComponentType,
    FC,
    HTMLAttributes,
    MouseEventHandler,
} from 'react';
import { linkVariants, RouterLink } from '@paypoteam/page-components';
import type { VariantProps } from 'class-variance-authority';
import Link from 'next/link';

import { cn } from '@/lib/utils';

import type { NavigationElementDataType } from '@/types';

export interface NavigationLinkProps extends HTMLAttributes<HTMLElement> {
    linkData: NavigationElementDataType;
    variant?: VariantProps<typeof linkVariants>;
    scrollById: boolean;
    onClick?: () => void;
}

const NavigationLink: FC<NavigationLinkProps> = ({
    linkData,
    variant,
    className,
    onClick,
    scrollById,
    ...props
}) => {
    const { dataTestId, relatedSectionId, ...linkConfig } = linkData;

    const handleLinkClick: MouseEventHandler<HTMLButtonElement> = event => {
        const relatedSectionPosition = document
            .querySelector(`#${relatedSectionId}`)
            ?.getBoundingClientRect().top;

        if (relatedSectionPosition) {
            const scrollPoint = relatedSectionPosition + window.scrollY - 200;
            window.scrollTo({ top: scrollPoint, behavior: 'smooth' });
        }

        (event.target as HTMLButtonElement).blur();
        onClick && onClick();
    };

    return (relatedSectionId && scrollById) || !linkConfig.href ? (
        <button
            className={cn(linkVariants(variant), className)}
            onClick={handleLinkClick}
            data-test-id={dataTestId}
            type="button"
            {...props}
            {...linkConfig}
        >
            {linkConfig.name}
        </button>
    ) : (
        <RouterLink
            LinkElement={
                Link as unknown as ComponentType<
                    AnchorHTMLAttributes<HTMLAnchorElement>
                >
            }
            linkData={linkConfig}
            variant={variant}
            className={className}
            onClick={onClick}
            data-test-id={dataTestId}
            {...props}
            {...linkConfig}
        />
    );
};

export default NavigationLink;
